/**
 * API Fetch Wrapper
 *
 * Centralized fetch with:
 * - Automatic request deduplication for GET requests
 * - Retry with exponential backoff
 * - Request/response interceptors
 * - Error handling
 * - Performance monitoring
 *
 * @example
 * ```ts
 * // Simple GET request (auto-deduped)
 * const data = await apiClient.get('/api/users');
 *
 * // POST with retry
 * const result = await apiClient.post('/api/users', {
 *   body: { name: 'John' },
 *   retry: 3
 * });
 * ```
 */

interface FetchWrapperConfig extends RequestInit {
  /** Enable request deduplication (ignored, kept for API compat) */
  dedupe?: boolean;
  /** Retry failed requests */
  retry?: number;
  /** Retry delay in ms */
  retryDelay?: number;
  /** Cache TTL (unused, kept for API compat) */
  cacheTTL?: number;
  /** Optimistic updates (unused, kept for API compat) */
  optimistic?: boolean;
}

class APIClient {
  private baseURL: string = '';
  private defaultConfig: FetchWrapperConfig = {
    dedupe: true,
    retry: 0,
    retryDelay: 1000,
  };

  /**
   * Configure base URL for all requests
   */
  setBaseURL(url: string): void {
    this.baseURL = url;
  }

  /**
   * Configure default options
   */
  setDefaultConfig(config: Partial<FetchWrapperConfig>): void {
    this.defaultConfig = { ...this.defaultConfig, ...config };
  }

  /**
   * GET request
   */
  async get<T>(url: string, config?: FetchWrapperConfig): Promise<T> {
    return this.request<T>(url, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(url: string, config?: FetchWrapperConfig): Promise<T> {
    return this.request<T>(url, { ...config, method: 'POST' });
  }

  /**
   * PUT request
   */
  async put<T>(url: string, config?: FetchWrapperConfig): Promise<T> {
    return this.request<T>(url, { ...config, method: 'PUT' });
  }

  /**
   * PATCH request
   */
  async patch<T>(url: string, config?: FetchWrapperConfig): Promise<T> {
    return this.request<T>(url, { ...config, method: 'PATCH' });
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: FetchWrapperConfig): Promise<T> {
    return this.request<T>(url, { ...config, method: 'DELETE' });
  }

  /**
   * Main request method
   */
  private async request<T>(url: string, config?: FetchWrapperConfig): Promise<T> {
    const fullURL = this.baseURL + url;
    const mergedConfig = { ...this.defaultConfig, ...config };
    const {
      dedupe: _dedupe,
      retry = 0,
      retryDelay = 1000,
      cacheTTL: _cacheTTL,
      optimistic: _optimistic,
      ...fetchOptions
    } = mergedConfig;

    const method = fetchOptions.method || 'GET';

    const fetchFn = async (retryCount = 0): Promise<T> => {
      try {
        const startTime = performance.now();

        const response = await fetch(fullURL, fetchOptions);

        const duration = performance.now() - startTime;

        // Log slow requests in development
        if (process.env.NODE_ENV === 'development' && duration > 1000) {
          console.warn(`[APIClient] Slow request (${duration.toFixed(0)}ms): ${method} ${url}`);
        }

        if (!response.ok) {
          // Handle 401 Unauthorized - attempt session refresh and retry once
          if (response.status === 401 && retryCount === 0) {
            try {
              const { recoverSession } = await import('@/lib/auth/sessionRecovery');
              if (await recoverSession()) {
                return fetchFn(retryCount + 1);
              }
            } catch (refreshErr) {
              console.error('[APIClient] Session recovery failed:', refreshErr);
            }
          }

          // Try to extract a meaningful error message from the response body
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          let errorCode: string | undefined;
          try {
            const errorBody = await response.clone().json();
            if (errorBody?.error && typeof errorBody.error === 'string') {
              errorMessage = errorBody.error;
            }
            if (errorBody?.errorCode && typeof errorBody.errorCode === 'string') {
              errorCode = errorBody.errorCode;
            }
          } catch {
            // Response wasn't JSON – keep the default HTTP status message
          }
          const httpError = new Error(errorMessage) as Error & { errorCode?: string };
          if (errorCode) {
            httpError.errorCode = errorCode;
          }
          throw httpError;
        }

        // Clone the response to prevent "body stream already read" errors
        // when the same deduplicated request is consumed by multiple callers
        const data: T = await response.clone().json();
        return data;
      } catch (error) {
        // Retry logic
        if (retryCount < retry) {
          const delay = retryDelay * Math.pow(2, retryCount);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchFn(retryCount + 1);
        }

        throw error;
      }
    };

    return fetchFn();
  }

  /**
   * Request interceptor (for adding auth headers, etc.)
   */
  interceptRequest(_interceptor: (config: FetchWrapperConfig) => FetchWrapperConfig): void {
    // Implement interceptor pattern if needed
    // For now, users can extend the class
  }

  /**
   * Response interceptor (for global error handling, etc.)
   */
  interceptResponse(_interceptor: (response: Response) => Response | Promise<Response>): void {
    // Implement interceptor pattern if needed
  }
}

// Singleton instance
export const apiClient = new APIClient();
